import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "@/hooks/useAuth";
import { usePets } from "@/hooks/usePets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, Camera, Upload } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface NovoPetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PetFormData {
  name: string;
  type: string;
  breed: string;
  color: string;
  birth_date: Date | undefined;
  unit_number: string;
  visible_to_residents: boolean;
  sex: string;
  size: string;
  weight: string;
  observations: string;
}

export function NovoPetModal({ open, onOpenChange }: NovoPetModalProps) {
  const { profile } = useAuth();
  const { createPet, updatePet, uploadPhoto, isCreating } = usePets();
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [selectedVaccinationCard, setSelectedVaccinationCard] = useState<File | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const vaccinationInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<PetFormData>({
    defaultValues: {
      visible_to_residents: false,
      unit_number: profile?.apartment_number || "",
    },
  });

  const watchedBirthDate = watch("birth_date");
  const watchedVisible = watch("visible_to_residents");

  const petTypes = [
    "Cachorro",
    "Gato",
    "Pássaro",
    "Peixe",
    "Hamster",
    "Coelho",
    "Outro"
  ];

  const petSizes = [
    "Pequeno",
    "Médio",
    "Grande",
    "Gigante"
  ];

  const handlePhotoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedPhoto(file);
    }
  };

  const handleVaccinationSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedVaccinationCard(file);
    }
  };

  const onSubmit = async (data: PetFormData) => {
    try {
      const petData = {
        name: data.name,
        type: data.type,
        breed: data.breed || undefined,
        color: data.color || undefined,
        birth_date: data.birth_date ? format(data.birth_date, "yyyy-MM-dd") : undefined,
        unit_number: data.unit_number || undefined,
        visible_to_residents: data.visible_to_residents,
        sex: data.sex || undefined,
        size: data.size || undefined,
        weight: data.weight ? parseFloat(data.weight) : undefined,
        observations: data.observations || undefined,
        owner_id: profile!.id,
        condominium_id: profile!.condominium_id,
        photo_url: undefined,
        vaccination_card_url: undefined,
      };

      // Primeiro criar o pet
      createPet(petData, {
        onSuccess: async (newPet) => {
          try {
            let photoUrl = '';
            let vaccinationUrl = '';

            // Upload da foto se selecionada
            if (selectedPhoto) {
              photoUrl = await uploadPhoto(selectedPhoto, newPet.id, 'photo');
            }

            // Upload da carteira de vacinação se selecionada
            if (selectedVaccinationCard) {
              vaccinationUrl = await uploadPhoto(selectedVaccinationCard, newPet.id, 'vaccination_card');
            }

            // Atualizar pet com URLs se necessário
            if (photoUrl || vaccinationUrl) {
              const updateData: any = {};
              if (photoUrl) updateData.photo_url = photoUrl;
              if (vaccinationUrl) updateData.vaccination_card_url = vaccinationUrl;
              
              updatePet({ id: newPet.id, ...updateData });
            }

            reset();
            setSelectedPhoto(null);
            setSelectedVaccinationCard(null);
            onOpenChange(false);
          } catch (error) {
            console.error("Erro no upload:", error);
          }
        }
      });
    } catch (error) {
      console.error("Erro ao criar pet:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Adicionar Novo Pet</DialogTitle>
          <DialogDescription>
            Cadastre um novo pet no sistema do condomínio.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Informações Básicas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome do Pet *</Label>
              <Input
                id="name"
                {...register("name", { required: "Nome é obrigatório" })}
                placeholder="Ex: Rex, Mimi"
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Tipo *</Label>
              <Select onValueChange={(value) => setValue("type", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {petTypes.map((type) => (
                    <SelectItem key={type} value={type.toLowerCase()}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="breed">Raça</Label>
              <Input
                id="breed"
                {...register("breed")}
                placeholder="Ex: Labrador, Persa"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="color">Cor</Label>
              <Input
                id="color"
                {...register("color")}
                placeholder="Ex: Marrom, Preto e branco"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data de Nascimento</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !watchedBirthDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {watchedBirthDate ? (
                      format(watchedBirthDate, "dd/MM/yyyy")
                    ) : (
                      <span>Selecione uma data</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={watchedBirthDate}
                    onSelect={(date) => setValue("birth_date", date)}
                    disabled={(date) => date > new Date()}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="unit_number">Unidade</Label>
              <Input
                id="unit_number"
                {...register("unit_number")}
                placeholder="Ex: Apt 101, Casa 5"
              />
            </div>
          </div>

          {/* Detalhes */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Sexo</Label>
              <Select onValueChange={(value) => setValue("sex", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="macho">Macho</SelectItem>
                  <SelectItem value="femea">Fêmea</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Porte</Label>
              <Select onValueChange={(value) => setValue("size", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {petSizes.map((size) => (
                    <SelectItem key={size} value={size.toLowerCase()}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="weight">Peso (kg)</Label>
              <Input
                id="weight"
                type="number"
                step="0.1"
                {...register("weight")}
                placeholder="Ex: 5.5"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="observations">Observações</Label>
            <Textarea
              id="observations"
              {...register("observations")}
              placeholder="Informações adicionais sobre o pet..."
              rows={3}
            />
          </div>

          {/* Visibilidade */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="visible"
              checked={watchedVisible}
              onCheckedChange={(checked) => setValue("visible_to_residents", !!checked)}
            />
            <Label htmlFor="visible">Visível para todos os moradores</Label>
          </div>

          {/* Upload de Fotos */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Foto do Pet</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => photoInputRef.current?.click()}
                  className="flex-1"
                >
                  <Camera className="mr-2 h-4 w-4" />
                  {selectedPhoto ? selectedPhoto.name : "Selecionar Foto"}
                </Button>
              </div>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoSelect}
                className="hidden"
              />
            </div>

            <div className="space-y-2">
              <Label>Carteira de Vacinação</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => vaccinationInputRef.current?.click()}
                  className="flex-1"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {selectedVaccinationCard ? selectedVaccinationCard.name : "Selecionar Arquivo"}
                </Button>
              </div>
              <input
                ref={vaccinationInputRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={handleVaccinationSelect}
                className="hidden"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isCreating}>
              {isCreating ? "Salvando..." : "Salvar Pet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}